/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICoreContext } from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    AsyncResult,
    CartLine,
    CartLineValidationResults,
    CartLineValidationResultsByLineId,
    CartsDataActions,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';

import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Validate the cart.
 * @param isQuantityLimitsFeatureEnabled -- Is the quantity limits feature enabled.
 * @param cart -- The cart state to validate.
 * @param telemetry -- The telemetry.
 * @param propsContext -- The props context.
 * @param shouldRetry -- Should retry.
 * @returns A list of line data validation failures.
 */
export async function validateCartAsync(
    isQuantityLimitsFeatureEnabled: boolean | undefined,
    cart: AsyncResult<ICartState>,
    telemetry: ITelemetry,
    propsContext: ICoreContext,
    shouldRetry: boolean = true
): Promise<CartLineValidationResultsByLineId | undefined> {
    if (!isQuantityLimitsFeatureEnabled) {
        return AsyncResult.resolve();
    }

    let cartResult: ICartState;
    let cartVersion: number | undefined;
    try {
        cartResult = await cart;
        cartVersion = cartResult.cart.Version;
    } catch (error) {
        telemetry.warning(error);
        telemetry.debug('Unable to get cart or cart version.');
        return AsyncResult.resolve();
    }

    if (cartVersion) {
        try {
            const validationResult: CartLineValidationResults = await CartsDataActions.validateForCheckoutAsync(
                { callerContext: propsContext.actionContext, bypassCache: 'none' },
                cartResult.cart.Id,
                cartVersion
            );

            return await AsyncResult.resolve(validationResult.ValidationResultsByLineId ?? undefined);
        } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- error type is not defined.
            if (shouldRetry && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                try {
                    await cartResult.refreshCart({});
                } catch (refreshError) {
                    telemetry.warning(refreshError);
                    telemetry.debug('Unable to refresh Cart');
                    return AsyncResult.resolve();
                }
                try {
                    await validateCartAsync(isQuantityLimitsFeatureEnabled, cart, telemetry, propsContext, false);
                    return AsyncResult.resolve();
                } catch (validateError) {
                    telemetry.warning(validateError);
                    telemetry.debug('Unable to validate Cart');
                    return AsyncResult.resolve();
                }
            }

            telemetry.warning(error);
            telemetry.debug('Unable to validate Cart');
            return AsyncResult.resolve();
        }
    } else {
        telemetry.warning('Cart version not found for Cart validation request.');
        return AsyncResult.resolve();
    }
}

/**
 * Check if cart line quantity update is success.
 * @param isQuantityLimitsFeatureEnabled -- Is quantity limits feature enabled.
 * @param cartResult -- The cart result.
 * @param telemetry -- The telemetry.
 * @param products -- The products.
 * @param cartLineToUpdate -- The cart line to update.
 * @param quantity -- The quantity.
 * @returns Boolean to indicate if the cart line quantity is updated.
 */
export async function isCartLineQuantityUpdatedAsync(
    isQuantityLimitsFeatureEnabled: boolean | undefined,
    cartResult: ICartState | undefined,
    telemetry: ITelemetry,
    products: SimpleProduct[] | undefined,
    cartLineToUpdate: CartLine,
    quantity: number
): Promise<boolean> {
    if (cartResult) {
        let isUsingDefaultOrderSettingsMax: boolean = false;
        if (ArrayExtensions.hasElements(products) && isQuantityLimitsFeatureEnabled) {
            const modifiedProduct: SimpleProduct | undefined = products.find(
                (product: SimpleProduct) => product.RecordId === cartLineToUpdate.ProductId
            );
            isUsingDefaultOrderSettingsMax = !!modifiedProduct && !!modifiedProduct.Behavior?.MaximumQuantity;
        } else {
            isUsingDefaultOrderSettingsMax = false;
        }

        const input = {
            cartLineId: cartLineToUpdate.LineId!.toString(),
            newQuantity: quantity,
            additionalProperties: { isUsingDefaultOrderSettingsMax }
        };

        try {
            const updateLineResult: ICartActionResult = await cartResult.updateCartLineQuantity(input);
            return await AsyncResult.resolve(updateLineResult.status === 'SUCCESS');
        } catch (error) {
            telemetry.warning((error as Error).message);
            telemetry.debug('Unable to update Cart Line quantity');
        }
    }

    return AsyncResult.resolve(false);
}

/**
 * Update cart line with cart action result.
 * @param isQuantityLimitsFeatureEnabled -- Is quantity limits feature enabled.
 * @param cartResult -- The cart result.
 * @param telemetry -- The telemetry.
 * @param products -- The products.
 * @param cartLineToUpdate -- The cart line to update.
 * @param quantity -- The quantity.
 * @returns Boolean to indicate if the cart line quantity is updated.
 */
export async function cartLineQuantityUpdatedAsync(
    isQuantityLimitsFeatureEnabled: boolean | undefined,
    cartResult: ICartState | undefined,
    telemetry: ITelemetry,
    products: SimpleProduct[] | undefined,
    cartLineToUpdate: CartLine,
    quantity: number
): Promise<ICartActionResult | undefined> {
    if (cartResult) {
        let isUsingDefaultOrderSettingsMax: boolean = false;
        if (ArrayExtensions.hasElements(products) && isQuantityLimitsFeatureEnabled) {
            const modifiedProduct: SimpleProduct | undefined = products.find(
                (product: SimpleProduct) => product.RecordId === cartLineToUpdate.ProductId
            );
            isUsingDefaultOrderSettingsMax = !!modifiedProduct && !!modifiedProduct.Behavior?.MaximumQuantity;
        } else {
            isUsingDefaultOrderSettingsMax = false;
        }

        const input = {
            cartLineId: cartLineToUpdate.LineId!.toString(),
            newQuantity: quantity,
            additionalProperties: { isUsingDefaultOrderSettingsMax }
        };

        try {
            const updateLineResult: ICartActionResult = await cartResult.updateCartLineQuantity(input);
            return await AsyncResult.resolve(updateLineResult);
        } catch (error) {
            telemetry.warning((error as Error).message);
            telemetry.debug('Unable to update Cart Line quantity');
        }
    }

    return AsyncResult.resolve();
}
